import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ListingsTable = ({ listings, onSort, sortField, sortDirection }) => {
  const SortableHeader = ({ field, children }) => (
    <th 
      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-base"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-2">
        {children}
        {sortField === field && (
          <Icon 
            name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
            size={14} 
            color="currentColor" 
          />
        )}
      </div>
    </th>
  );

  const getPortalBadge = (portal) => {
    const config = {
      domain: { color: 'bg-blue-500/10 text-blue-600', label: 'Domain' },
      realestate: { color: 'bg-orange-500/10 text-orange-600', label: 'REA' }
    };
    return config?.[portal] || config?.domain;
  };

  const getCompletionColor = (rate) => {
    if (rate >= 90) return 'text-success';
    if (rate >= 70) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-sm overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/30">
            <tr>
              <SortableHeader field="property">Property</SortableHeader>
              <SortableHeader field="address">Address</SortableHeader>
              <SortableHeader field="portal">Portal</SortableHeader>
              <SortableHeader field="analysisCount">Analyses</SortableHeader>
              <SortableHeader field="completionRate">Completion Rate</SortableHeader>
              <SortableHeader field="errorCount">Errors</SortableHeader>
              <SortableHeader field="lastAnalyzed">Last Analyzed</SortableHeader>
              <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {listings?.map((listing) => {
              const portalBadge = getPortalBadge(listing?.portal);
              return (
                <tr key={listing?.id} className="hover:bg-muted/20 transition-base">
                  <td className="px-4 py-4">
                    <div className="w-16 h-12 rounded overflow-hidden flex-shrink-0">
                      <Image 
                        src={listing?.image} 
                        alt={listing?.imageAlt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="max-w-xs">
                      <p className="text-sm font-medium text-foreground truncate">{listing?.address}</p>
                      <p className="text-xs text-muted-foreground">{listing?.suburb}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${portalBadge?.color}`}>
                      {portalBadge?.label}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-foreground text-data text-center">{listing?.analysisCount}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden min-w-[60px]">
                        <div 
                          className="h-full bg-primary transition-all duration-300"
                          style={{ width: `${listing?.completionRate}%` }}
                        />
                      </div>
                      <span className={`text-sm font-medium text-data ${getCompletionColor(listing?.completionRate)}`}>
                        {listing?.completionRate}%
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-foreground text-data text-center">
                    {listing?.errorCount > 0 ? (
                      <span className="text-error font-medium">{listing?.errorCount}</span>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm text-foreground text-data">{listing?.lastAnalyzed}</td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="sm" iconName="Eye">
                        View
                      </Button>
                      <Button variant="ghost" size="sm" iconName="MoreVertical" />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden divide-y divide-border">
        {listings?.map((listing) => {
          const portalBadge = getPortalBadge(listing?.portal);
          return (
            <div key={listing?.id} className="p-4">
              <div className="flex gap-3 mb-3">
                <div className="w-20 h-16 rounded overflow-hidden flex-shrink-0">
                  <Image 
                    src={listing?.image} 
                    alt={listing?.imageAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-foreground mb-1 line-clamp-2">{listing?.address}</h4>
                  <p className="text-xs text-muted-foreground mb-2">{listing?.suburb}</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${portalBadge?.color}`}>
                    {portalBadge?.label}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground text-caption mb-1">Analyses</p>
                  <p className="text-foreground text-data font-medium">{listing?.analysisCount}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground text-caption mb-1">Errors</p>
                  <p className={`text-data font-medium ${listing?.errorCount > 0 ? 'text-error' : 'text-muted-foreground'}`}>
                    {listing?.errorCount}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground text-caption mb-2">Completion Rate</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                      <div 
                        className="h-full bg-primary transition-all duration-300"
                        style={{ width: `${listing?.completionRate}%` }}
                      />
                    </div>
                    <span className={`text-sm font-medium text-data ${getCompletionColor(listing?.completionRate)}`}>
                      {listing?.completionRate}%
                    </span>
                  </div>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground text-caption mb-1">Last Analyzed</p>
                  <p className="text-foreground text-data">{listing?.lastAnalyzed}</p>
                </div>
              </div>
              <Button variant="outline" size="sm" iconName="Eye" className="w-full">
                View Details
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ListingsTable;